import { IInventoryDeviceService } from '../models/inventory-device';

/**
 * Base rule: the next service date would be derived from the latest service plus the interval
 * when both an interval and at least one service exist. Only in this state a manual override is possible.
 */
export function canOverrideNextService(services: IInventoryDeviceService[] | null | undefined, serviceInterval: number | null | undefined): boolean {
  return (services?.length ?? 0) > 0 && serviceInterval > 0;
}

/**
 * Mirrors the backend rule (InventoryDevice.IsNextServiceComputed): the next service date is computed
 * when the base rule applies and the date was not manually overridden. Otherwise the date is entered manually.
 */
export function isNextServiceComputed(services: IInventoryDeviceService[] | null | undefined, serviceInterval: number | null | undefined, overridden = false): boolean {
  return canOverrideNextService(services, serviceInterval) && !overridden;
}

/**
 * Preview of the value the backend would compute: latest execution date + interval months.
 * Returns null when the base rule does not apply. Ignores an override, so the computed value can be restored.
 */
export function computeNextService(services: IInventoryDeviceService[] | null | undefined, serviceInterval: number | null | undefined): Date | null {
  if (!canOverrideNextService(services, serviceInterval)) {
    return null;
  }

  const latest = services
    .map(service => new Date(service.executionDate))
    .reduce((max, date) => (date > max ? date : max));

  return addMonthsClamped(latest, serviceInterval);
}

/**
 * Same semantics as .NET DateTime.AddMonths: the day is clamped to the last day of the target month
 * (31.01. + 1 month = 28.02.), unlike Date.setMonth which overflows into the following month.
 */
export function addMonthsClamped(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), lastDayOfTargetMonth));
  return target;
}
